import React, { useState } from 'react';
import { MapPinIcon, SearchIcon, FilterIcon, XIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function MapViewPage({
  navigateTo,
  setSelectedPlotId
}: {
  navigateTo: (page: string) => void;
  setSelectedPlotId: (id: string) => void;
}) {
  const {
    language,
    t
  } = useLanguage();
  const [activePin, setActivePin] = useState<string | null>(null);
  const handleViewDetails = (id: string) => {
    setSelectedPlotId(id);
    navigateTo('plotDetails');
  };
  const getPinColor = (type: string) => {
    switch (type) {
      case 'agricultural':
        return 'text-[#4CAF50]';
      // Green
      case 'residential':
        return 'text-[#2196F3]';
      // Blue
      case 'commercial':
        return 'text-[#FF9800]';
      // Orange
      default:
        return 'text-[#006A4E]';
    }
  };
  return <div className="h-[calc(100vh-120px)] flex flex-col space-y-4" data-id="element-930">
      <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200" data-id="element-931">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-932">Land Records Map</h1>
        <div className="flex space-x-2" data-id="element-933">
          <div className="relative" data-id="element-934">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-935" />
            <input type="text" placeholder="Search by Khatian or Dag..." className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-[#006A4E] focus:border-[#006A4E] w-64" data-id="element-936" />
          </div>
          <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center" data-id="element-937">
            <FilterIcon className="w-4 h-4 mr-2" data-id="element-938" /> Filter
          </button>
        </div>
      </div>

      <div className="flex-1 relative bg-gray-100 rounded-xl border border-gray-200 overflow-hidden shadow-inner" data-id="element-939">
        {/* Actual Map Image Background */}
        <div className="absolute inset-0 overflow-auto" data-id="element-940">
          <div className="relative w-[1200px] h-[800px] md:w-full md:h-full" data-id="element-941">
            <img src="https://cdn.magicpatterns.com/uploads/9K6dKez36KyrJFQhveVRS6/image.png" alt="Mouza Map" className="w-full h-full object-cover opacity-90" data-id="element-942" />

            {/* Map Pins - Only show user's plots */}
            {samplePlots.filter(p => ['PLOT-001', 'PLOT-002', 'PLOT-003', 'PLOT-004', 'PLOT-005'].includes(p.id)).map(plot => <div key={plot.id} className="absolute transform -translate-x-1/2 -translate-y-full cursor-pointer group z-10 transition-transform hover:scale-110" style={{
            left: plot.mapCoords.x,
            top: plot.mapCoords.y
          }} onClick={() => setActivePin(plot.id)} data-id="element-943">
                  <MapPinIcon className={`w-10 h-10 ${getPinColor(plot.type)} drop-shadow-lg transition-colors`} fill="white" data-id="element-944" />
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-sm" data-id="element-945"></div>

                  {/* Hover Tooltip (shows when not active) */}
                  {activePin !== plot.id && <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block whitespace-nowrap bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none" data-id="element-946">
                      {t('dag')}: {plot.dag}
                    </div>}
                </div>)}

            {/* Mini Card Popup */}
            {activePin && <div className="absolute z-20 transform -translate-x-1/2 translate-y-4 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200" style={{
            left: samplePlots.find(p => p.id === activePin)?.mapCoords.x,
            top: samplePlots.find(p => p.id === activePin)?.mapCoords.y
          }} data-id="element-947">
                <div className="bg-[#006A4E] h-2 w-full" data-id="element-948"></div>
                <div className="p-4 relative" data-id="element-949">
                  <button onClick={e => {
                e.stopPropagation();
                setActivePin(null);
              }} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 bg-gray-50 rounded-full p-1 transition-colors" data-id="element-950">
                    <XIcon className="w-4 h-4" data-id="element-951" />
                  </button>

                  {(() => {
                const plot = samplePlots.find(p => p.id === activePin)!;
                return <div data-id="element-952">
                        <div className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider mb-2" style={{
                    backgroundColor: getPinColor(plot.type).replace('text-[', '').replace(']', '') + '20',
                    color: getPinColor(plot.type).replace('text-[', '').replace(']', '')
                  }} data-id="element-953">
                          {t(plot.type as any)}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 pr-6 leading-tight mb-1" data-id="element-954">
                          {language === 'bn' ? plot.nameBn : plot.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-4 flex items-center" data-id="element-955">
                          <MapPinIcon className="w-3 h-3 mr-1" data-id="element-956" />
                          {language === 'bn' ? plot.districtBn : plot.district},{' '}
                          {language === 'bn' ? plot.upazilaBn : plot.upazila}
                        </p>

                        <div className="grid grid-cols-2 gap-3 text-sm mb-5" data-id="element-957">
                          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100" data-id="element-958">
                            <span className="text-gray-500 text-xs block mb-0.5" data-id="element-959">
                              {t('khatian')}
                            </span>
                            <span className="font-bold text-gray-900" data-id="element-960">
                              {plot.khatian}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100" data-id="element-961">
                            <span className="text-gray-500 text-xs block mb-0.5" data-id="element-962">
                              {t('dag')}
                            </span>
                            <span className="font-bold text-gray-900" data-id="element-963">
                              {plot.dag}
                            </span>
                          </div>
                        </div>

                        <button onClick={() => handleViewDetails(plot.id)} className="w-full py-2.5 bg-[#006A4E] text-white rounded-lg font-medium hover:bg-[#004D3A] transition-colors text-sm flex items-center justify-center shadow-sm" data-id="element-964">
                          {t('viewDetails')}
                          <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-965" />
                        </button>
                      </div>;
              })()}
                </div>
              </div>}
          </div>
        </div>

        {/* Legend Overlay */}
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-gray-200 z-10" data-id="element-966">
          <h4 className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider" data-id="element-967">
            {t('legend')}
          </h4>
          <div className="space-y-2 text-sm" data-id="element-968">
            <div className="flex items-center" data-id="element-969">
              <MapPinIcon className="w-4 h-4 text-[#4CAF50] mr-2" fill="white" data-id="element-970" />
              <span className="text-gray-700" data-id="element-971">{t('agricultural')}</span>
            </div>
            <div className="flex items-center" data-id="element-972">
              <MapPinIcon className="w-4 h-4 text-[#2196F3] mr-2" fill="white" data-id="element-973" />
              <span className="text-gray-700" data-id="element-974">{t('residential')}</span>
            </div>
            <div className="flex items-center" data-id="element-975">
              <MapPinIcon className="w-4 h-4 text-[#FF9800] mr-2" fill="white" data-id="element-976" />
              <span className="text-gray-700" data-id="element-977">{t('commercial')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}